import React, { useState } from 'react'
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Switch,
  Select,
  MenuItem
} from '@mui/material'
import { Home, LocalParking, Layers, ContentCopy, Check, Edit, Tune } from '@mui/icons-material'
import { Property } from '../types'
import { PriceColumn } from './PriceColumn'
import { StatusColumn } from './StatusColumn'
import { AdditionalCostsColumn } from './AdditionalCostsColumn'
import { EditAreaModal } from './EditAreaModal'
import { EditTitleModal } from './EditTitleModal'
import { EditFloorModal } from './EditFloorModal'
import { useNotify } from 'react-admin'
import { supabase } from '../supabaseClient'
import { type PromoBadge } from '@repo/supabase'
import type { PriceBadgeData } from '../hooks/usePriceBadge'

interface PropertyCardUnifiedProps {
  record: Property & {
    total_price?: string
    price_per_sqm?: string
    area_total?: number
    promo_badge_id?: string | null
  }
  priceBadgeData?: PriceBadgeData
  promoBadges?: PromoBadge[]
  promoModuleEnabled?: boolean
  onManageKafelki?: () => void
}

export const PropertyCardUnified: React.FC<PropertyCardUnifiedProps> = ({ record, priceBadgeData, promoBadges = [], promoModuleEnabled = false, onManageKafelki }) => {
  const notify = useNotify()
  const [copied, setCopied] = useState(false)
  const [areaModalOpen, setAreaModalOpen] = useState(false)
  const [titleModalOpen, setTitleModalOpen] = useState(false)
  const [floorModalOpen, setFloorModalOpen] = useState(false)
  const [isHidden, setIsHidden] = useState<boolean>(record.is_price_badge_hidden ?? false)
  const [promoBadgeId, setPromoBadgeId] = useState<string | null>(record.promo_badge_id ?? null)
  const isEligible = priceBadgeData?.isEligible ?? false
  const maxHistoricalPrice = priceBadgeData?.maxHistoricalPrice ?? null

  const handlePriceBadgeChange = async (checked: boolean) => {
    const newIsHidden = !checked
    setIsHidden(newIsHidden)
    const { data } = await supabase.rpc('set_property_price_badge', {
      p_property_id: record.id,
      p_is_hidden: newIsHidden
    })
    const result = data as { success: boolean; error?: string } | null
    if (result && !result.success) {
      setIsHidden(!newIsHidden)
      notify(result.error ?? 'Nieznany błąd', { type: 'error' })
    }
  }

  const handlePromoBadgeChange = async (value: string) => {
    const newBadgeId = value === '' ? null : value
    const prev = promoBadgeId
    setPromoBadgeId(newBadgeId)
    const { data } = await supabase.rpc('set_property_promo_badge', {
      p_property_id: record.id,
      p_badge_id: newBadgeId as string
    })
    const result = data as { success: boolean; error?: string } | null
    if (result && !result.success) {
      setPromoBadgeId(prev)
      notify(result.error ?? 'Nieznany błąd', { type: 'error' })
    }
  }

  if (!record) return null

  const handleCopyId = () => {
    navigator.clipboard.writeText(record.id)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const getPropertyTypeIcon = (type: string) => {
    switch (type) {
      case 'lokal-mieszkalny':
        return <Home fontSize="small" />
      case 'miejsce-postojowe':
        return <LocalParking fontSize="small" />
      default:
        return <Layers fontSize="small" />
    }
  }

  const getPropertyTypeLabel = (type: string) => {
    const typeLabels: { [key: string]: string } = {
      'lokal-mieszkalny': 'Lokal mieszkalny',
      'miejsce-postojowe': 'Miejsce postojowe',
      'niezadaszone-miejsce-wylacznego-korzystania': 'Niezadaszone miejsce do wyłącznego korzystania',
      'house': 'Dom'
    }
    return typeLabels[type] || type
  }

  const isBadgeActive = !isHidden

  return (
    <Card
      sx={{
        width: '100%',
        maxWidth: '100%',
        borderRadius: 2,
        overflow: 'hidden',
        '&:hover': {
          boxShadow: 3,
          transform: 'translateY(-2px)',
          transition: 'all 0.2s ease-in-out'
        }
      }}
    >
      <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Header - Top Row */}
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            {getPropertyTypeIcon(record.property_type)}
            <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.75rem' }}>
              {getPropertyTypeLabel(record.property_type)}
            </Typography>
          </Box>
          <Tooltip title={copied ? 'Skopiowano!' : `Kopiuj ID: ${record.id}`}>
            <IconButton
              size="small"
              onClick={handleCopyId}
              sx={{
                backgroundColor: copied ? 'success.light' : 'primary.light',
                color: copied ? 'success.contrastText' : 'primary.contrastText',
                '&:hover': {
                  backgroundColor: copied ? 'success.main' : 'primary.main',
                },
                width: 28,
                height: 28,
              }}
            >
              {copied ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
            </IconButton>
          </Tooltip>
        </Box>

        {/* Title with Label */}
        <Box sx={{
          p: 1.5,
          backgroundColor: 'grey.50',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'grey.200'
        }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 'bold', mb: 0.5 }}>
            Nazwa
          </Typography>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem', lineHeight: 1.2 }}>
              {record.title}
            </Typography>
            <Tooltip title="Edytuj nazwę" disableInteractive>
              <IconButton aria-label="Edytuj nazwę" size="small" onClick={() => setTitleModalOpen(true)} sx={{ p: 0.25 }}>
                <Edit sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Basic Details */}
        <Box display="flex" flexWrap="wrap" gap={2}>
          {record.area_total && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography variant="body2" color="text.secondary">
                📐 {record.area_total} m²
              </Typography>
              <Tooltip title="Edytuj metraż">
                <IconButton aria-label="Edytuj metraż" size="small" onClick={() => setAreaModalOpen(true)} sx={{ p: 0.25 }}>
                  <Edit sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          {record.rooms_total && (
            <Typography variant="body2" color="text.secondary">
              🏠 {record.rooms_total} {record.property_type === 'lokal uslugowy' ? 'pomieszczeń' : 'pokoi'}
            </Typography>
          )}
          {/* Piętro renderowane zawsze — także gdy puste, żeby dało się je uzupełnić. */}
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant="body2" color="text.secondary" data-testid="floor-display">
              🏢 {record.floor_number === null || record.floor_number === undefined
                ? 'Brak piętra'
                : record.floor_number === 0 ? 'Parter' : `${record.floor_number} piętro`}
            </Typography>
            <Tooltip title="Edytuj piętro" disableInteractive>
              <IconButton aria-label="Edytuj piętro" size="small" onClick={() => setFloorModalOpen(true)} sx={{ p: 0.25 }}>
                <Edit sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>


        {/* Second Row - Price and Status in 2 columns */}
        <Box display="flex" gap={2} width="100%">
          {/* Price Column - 50% width */}
          <Box sx={{
            flex: '1 1 50%',
            maxWidth: '50%',
            p: 1.5,
            backgroundColor: 'grey.50',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.200',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {record.property_type === 'lokal uslugowy' ? 'Cena netto' : 'Cena'}
            </Typography>
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'flex-start', minHeight: '62px', overflow: 'hidden', pt: 0.75 }}>
              <PriceColumn
                record={record}
                key={`price-${record.id}-${record.total_price}-${record.price_per_sqm}`}
                originalPrice={isBadgeActive && maxHistoricalPrice != null ? maxHistoricalPrice : undefined}
              />
            </Box>
          </Box>

          {/* Status Column - 50% width */}
          <Box sx={{
            flex: '1 1 50%',
            maxWidth: '50%',
            p: 1.5,
            backgroundColor: 'grey.50',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.200',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Status
            </Typography>
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'flex-start', minHeight: '62px', overflow: 'hidden', pt: 0.75 }}>
              <StatusColumn
                record={record}
                key={`status-${record.id}-${record.status_code}`}
              />
            </Box>
          </Box>
        </Box>

        {/* Additional Costs Row - Full width (conditionally rendered by component itself) */}
        <AdditionalCostsColumn
          record={record}
          key={`additional-costs-${record.id}`}
        />

        {/* Promocja — jeden kontener: widoczność + wybór kafelka.
            Pokazywany tylko gdy obniżka ceny (isEligible) — analogicznie do widgetu:
            brak promocji (np. miejsce postojowe bez obniżki) = brak kafelka. */}
        {isEligible && (
          <Box
            data-testid="promocja-container"
            sx={{
              p: 1.5,
              backgroundColor: isBadgeActive ? 'warning.50' : 'grey.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: isBadgeActive ? 'warning.300' : 'grey.200',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.25,
              transition: 'background-color 0.2s, border-color 0.2s'
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 'bold', color: isBadgeActive ? 'warning.800' : 'text.secondary' }}>
                  Promocja
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.25 }}>
                  {isBadgeActive ? 'Widoczna dla klientów' : 'Ukryta dla klientów'}
                </Typography>
              </Box>
              <Switch
                checked={isBadgeActive}
                onChange={(e) => handlePriceBadgeChange(e.target.checked)}
                size="small"
                color="warning"
              />
            </Box>

            {/* Wybór kafelka — w tym samym kontenerze; tylko gdy moduł kafelków włączony i promocja widoczna */}
            {promoModuleEnabled && isBadgeActive && (
              <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                <Typography variant="body2" sx={{ fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 'bold', color: 'text.secondary' }}>
                  Kafelek
                </Typography>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Select
                    value={promoBadgeId ?? ''}
                    onChange={(e) => handlePromoBadgeChange(e.target.value as string)}
                    size="small"
                    displayEmpty
                    inputProps={{ 'data-testid': 'kafelek-select' }}
                    sx={{ fontSize: '0.8rem', minWidth: 150 }}
                  >
                    <MenuItem value="">Domyślny (PROMOCJA)</MenuItem>
                    {promoBadges.map(badge => (
                      <MenuItem key={badge.id} value={badge.id}>{badge.label}</MenuItem>
                    ))}
                  </Select>
                  {onManageKafelki && (
                    <Tooltip title="Zarządzaj kafelkami">
                      <IconButton size="small" aria-label="Zarządzaj kafelkami" onClick={onManageKafelki}>
                        <Tune fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </CardContent>

      {/* Montowane dopiero po otwarciu — stan początkowy pola zawsze z aktualnego rekordu,
          a lista tysiąca kart nie trzyma nieużywanych dialogów. */}
      {titleModalOpen && (
        <EditTitleModal
          open
          onClose={() => setTitleModalOpen(false)}
          propertyId={record.id}
          currentTitle={record.title}
        />
      )}

      {floorModalOpen && (
        <EditFloorModal
          open
          onClose={() => setFloorModalOpen(false)}
          propertyId={record.id}
          title={record.title}
          currentFloor={record.floor_number ?? null}
        />
      )}

      {record.area_total != null && (
        <EditAreaModal
          open={areaModalOpen}
          onClose={() => setAreaModalOpen(false)}
          propertyId={record.id}
          title={record.title}
          currentArea={record.area_total}
          totalPrice={record.total_price != null ? Number(record.total_price) : null}
        />
      )}
    </Card>
  )
}
